
import { DistanceData, TendencyPieData, SwingTendency } from './types';
import { PuttingResult } from "@/types/puttingResults";
import { supabase } from "@/integrations/supabase/client";

// Traduction des directions selon la langue
const getDirectionTranslations = (currentLanguage: string) => {
  switch (currentLanguage) {
    case 'en':
      return {
        left: 'Left',
        center: 'Center', 
        right: 'Right'
      };
    case 'es':
      return {
        left: 'Izquierda',
        center: 'Centro',
        right: 'Derecha'
      };
    default:
      return {
        left: 'Gauche',
        center: 'Centre',
        right: 'Droite'
      };
  }
};

// Create pie chart data for a distance
export const createPieChartData = (distanceData: DistanceData, currentLanguage: string = 'fr'): TendencyPieData[] => {
  const { left, center, right, total } = distanceData;
  if (total === 0) return [];
  
  const translations = getDirectionTranslations(currentLanguage);
  
  return [
    { name: translations.left, value: left, color: '#3b82f6' },
    { name: translations.center, value: center, color: '#10b981' },
    { name: translations.right, value: right, color: '#ef4444' }
  ].filter(item => item.value > 0); // Only include non-zero values
};

// Percentage formatter for tooltip
export const percentageFormatter = (value: number, total: number): string => {
  if (total === 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

// Get only results based on selected level
export const getResultsByLevel = (results: PuttingResult[], selectedLevel: string | null) => {
  if (!selectedLevel) return results;
  return results.filter(result => String(result.test_level) === selectedLevel);
};

// Extract swing tendencies from results
export const getTendenciesFromResults = (results: PuttingResult[]): SwingTendency[] => {
  const tendencies: SwingTendency[] = [];
  
  results.forEach(result => {
    const metadata = result.metadata || {};
    const testId = result.test_id || '';
    const testLevel = String(result.test_level || '');
    const testDate = result.test_date || new Date().toISOString();
    
    // Process iron distances
    const ironDistances = ['70m', '90m', '110m', '130m', '150m'];
    ironDistances.forEach(distance => {
      const tendencyKey = `iron_${distance}_tendency`;
      const pointsKey = `iron_${distance}_points`;
      
      if (metadata[tendencyKey]) {
        const tendency = String(metadata[tendencyKey]).toLowerCase();
        let normalizedTendency: 'left' | 'center' | 'right' = 'center';
        
        if (tendency.includes('left')) {
          normalizedTendency = 'left';
        } else if (tendency.includes('right')) {
          normalizedTendency = 'right';
        } else if (tendency.includes('center')) {
          normalizedTendency = 'center';
        }
        
        const points = metadata[pointsKey] ? Number(metadata[pointsKey]) : 0;
        
        tendencies.push({
          id: `${result.id}_iron_${distance}`,
          user_id: result.user_id || '',
          test_id: testId,
          club_type: 'iron',
          test_date: testDate,
          created_at: new Date().toISOString(), // Use current date instead of accessing non-existent property
          distance: distance,
          tendency: normalizedTendency,
          test_level: testLevel,
          points: points,
          test_name: result.test_name
        });
      }
    });
    
    // Process fairway wood distances
    const fairwayWoodDistances = ['130m', '150m', '170m'];
    fairwayWoodDistances.forEach(distance => {
      const tendencyKey = `fairwaywood_${distance}_tendency`;
      const pointsKey = `fairwaywood_${distance}_points`;
      
      if (metadata[tendencyKey]) {
        const tendency = String(metadata[tendencyKey]).toLowerCase();
        let normalizedTendency: 'left' | 'center' | 'right' = 'center';
        
        if (tendency.includes('left')) {
          normalizedTendency = 'left';
        } else if (tendency.includes('right')) {
          normalizedTendency = 'right';
        } else if (tendency.includes('center')) {
          normalizedTendency = 'center';
        }
        
        const points = metadata[pointsKey] ? Number(metadata[pointsKey]) : 0;
        
        tendencies.push({
          id: `${result.id}_fairwaywood_${distance}`,
          user_id: result.user_id || '',
          test_id: testId,
          club_type: 'fairwayWood',
          test_date: testDate,
          created_at: new Date().toISOString(), // Use current date instead of accessing non-existent property
          distance: distance,
          tendency: normalizedTendency,
          test_level: testLevel,
          points: points,
          test_name: result.test_name
        });
      }
    });
  });
  
  return tendencies;
};

// Save a tendency to the database
export const saveTendency = async (
  userId: string,
  testId: string,
  clubType: string,
  distance: string,
  tendency: 'left' | 'center' | 'right',
  level: string | null
): Promise<boolean> => {
  try {
    // Initialize effective level
    let effectiveLevel = level;
    
    // 1. If level is not provided, try to extract it from testId (e.g., "driver-l3" -> "3")
    if (!effectiveLevel && testId) {
      const levelMatch = testId.match(/-l(\d+)/i);
      if (levelMatch && levelMatch[1]) {
        effectiveLevel = levelMatch[1];
        console.log(`Niveau extrait ${effectiveLevel} du testId ${testId}`);
      }
    }
    
    // 2. If still no level, determine it from current page URL
    if (!effectiveLevel) {
      if (typeof window !== 'undefined' && window.location) {
        const pathname = window.location.pathname;
        
        if (pathname.includes('/level1') || pathname === '/level1training') {
          effectiveLevel = '1';
          console.log("Niveau défini à 1 depuis Level1Training");
        } else if (pathname.includes('/level2') || pathname === '/level2training') {
          effectiveLevel = '2';
          console.log("Niveau défini à 2 depuis Level2Training");
        } else if (pathname.includes('/level3') || pathname === '/level3training' || pathname.includes('/driver')) {
          effectiveLevel = '3';
          console.log("Niveau défini à 3 depuis Level3Training ou DriverTraining");
        }
      }
    }
    
    // 3. Last resort default: ensure we never have a null level
    if (!effectiveLevel) {
      // Default to level 3 if we still don't have a level
      effectiveLevel = '3';
      console.log("Niveau par défaut défini à 3");
    }
    
    console.log(`Enregistrement de la tendance: test_id: ${testId}, club_type: ${clubType}, niveau: ${effectiveLevel}`);
    
    const { data, error } = await supabase
      .from('swing_tendencies')
      .insert([{ 
        user_id: userId,
        test_id: testId,
        club_type: clubType,
        distance: distance,
        tendency: tendency,
        test_level: effectiveLevel, // This will now never be null
        test_date: new Date().toISOString()
      }]);
      
    if (error) {
      console.error("Erreur lors de l'enregistrement de la tendance:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Exception lors de l'enregistrement de la tendance:", error);
    return false;
  }
};

// Re-export all from smaller files
export * from './types';
export * from './constants';
export * from './chartUtils';
export * from './ironUtils';
export * from './fairwayWoodUtils';
export * from './dbOperations';

// Ajouter l'export pour les fonctions liées au driver
export * from './driverUtils';
